import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import TextPlugin from 'gsap/dist/TextPlugin';
import { gsap } from 'gsap/dist/gsap';
import Image from 'next/image';
import { FC, useRef } from 'react';

import styles from './b2b-scrolling-content.module.scss';
import { SplitChar } from '@/components/splitchar/splitchar';

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(TextPlugin);

export interface B2BScrollingContentProps {
    title: string;
    description: string;
    sections: {
        title: string;
        description: string;
        image: string;
    }[];
}

export const B2BScrollingContent: FC<B2BScrollingContentProps> = (props) => {
    const { title, description, sections = [] } = props;

    const containerRef = useRef<HTMLDivElement>(null);

    useGSAP(
        () => {
            const selector = gsap.utils.selector(containerRef.current);
            const sections = gsap.utils.toArray<HTMLElement>(selector(`.${styles.section}`));

            let mm = gsap.matchMedia();

            gsap.timeline({
                scrollTrigger: {
                    trigger: containerRef.current,
                    pinnedContainer: containerRef.current,
                    pin: true,
                    start: 'center center',
                    end: '+=100%',
                    scrub: 1,
                },
            });

            let masterTl = gsap.timeline({
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: 'top center+=40%',
                    end: 'bottom top',
                    scrub: true,
                },
            });

            gsap.set(sections, { autoAlpha: 0 });

            for (let i = 0; i < sections.length; i++) {
                const section = sections[i];

                const left = gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(section)(`.${styles.left}`))[0];

                const prvSection = sections[i - 1];
                if (prvSection) {
                    const prvTitle = gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(prvSection)(`.${styles.left}`))[0];
                    const tlOut = animateSection(prvSection, prvTitle, 'out');
                    masterTl.add(tlOut);
                }

                const tlIn = animateSection(section, left, 'in', i === 0 ? '+=1' : '<');
                masterTl.to(section, { xPercent: -100 * i, x: 0 }, '<');
                masterTl.add(tlIn, '-=0.4');
            }
        },
        {
            scope: containerRef,
            dependencies: [sections],
        }
    );

    const animateSection = (section: HTMLElement, leftElm: HTMLElement, animationType: 'in' | 'out', position?: gsap.Position) => {
        const sectionSelector = gsap.utils.selector(section);
        const contentItems = gsap.utils.toArray<HTMLDivElement>(sectionSelector(`.${styles.right}`));
        const number = gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(leftElm)(`.${styles.number}`));
        const titleSplits = gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(leftElm)(`.${styles.title} span`));
        const desSplits = gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(leftElm)(`.${styles.description} span`));

        const tl = gsap.timeline({});

        if (animationType === 'in') {
            tl.to(section, { autoAlpha: 1 }, position);
            tl.fromTo(
                [contentItems, number, titleSplits, desSplits],
                {
                    autoAlpha: 0,
                },
                {
                    duration: 1,
                    autoAlpha: 1,
                    stagger: {
                        from: 'start',
                        each: 0.02,
                    },
                },
                '>-=0.5'
            );
        } else {
            tl.to(
                [contentItems, number, titleSplits, desSplits],
                {
                    duration: 0.5,
                    autoAlpha: 0,
                    stagger: {
                        from: 'start',
                        each: 0.005,
                    },
                },
                '+=1'
            );
        }

        return tl;
    };

    return (
        <div ref={containerRef} className={styles.container}>
            <div className={styles.head}>
                <div className={styles.title}>{title}</div>
                <div className={styles.description} dangerouslySetInnerHTML={{ __html: description || '' }} />
            </div>

            <div className={styles.scrolling}>
                {sections.map((section, index) => (
                    <section className={styles.section}>
                        <div className={styles.left}>
                            <div className={styles.number}>{index + 1}</div>
                            <div className={styles.title}>
                                <SplitChar text={section.title} />
                            </div>
                            <div className={styles.description}>
                                <SplitChar text={section.description} />
                            </div>
                        </div>

                        <div className={styles.right}>
                            <Image
                                src={section.image}
                                alt=""
                                style={{ objectFit: 'contain', objectPosition: 'center' }}
                                fill={true}
                                loading={'eager'}
                                sizes="(max-width: 638px) 98vw,  (max-width: 998px) 43vw, 46vw"
                                className={styles.image}
                            />
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );
};
